import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Users, 
  FileText, 
  TrendingUp, 
  TrendingDown,
  Target,
  Crown,
  MessageCircle,
  FileSearch,
  AlertTriangle,
  CheckCircle2,
  Clock,
  BarChart3,
  PieChart,
  Activity
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple Bar Chart Component
const BarChart = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end justify-between gap-2" style={{ height }}>
      {data.map((item, idx) => (
        <div key={idx} className="flex flex-col items-center flex-1">
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: `${(item.count / maxValue) * 100}%` }}
            transition={{ delay: idx * 0.1, duration: 0.5 }}
            className="w-full bg-gradient-to-t from-gold-600 to-gold-400 rounded-t-lg min-h-[4px] relative group"
          >
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-navy-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {item.count} casos
            </div>
          </motion.div>
          <span className="text-xs text-navy-500 mt-2">{item.month}</span>
        </div>
      ))}
    </div>
  );
};

// Simple Donut Chart Component
const DonutChart = ({ data, size = 150 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  let currentAngle = 0;
  
  const colors = ["#10B981", "#F59E0B", "#EF4444", "#6B7280"];
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="transform -rotate-90">
        {data.map((item, idx) => {
          const percentage = total > 0 ? item.value / total : 0;
          const angle = percentage * 360;
          const startAngle = currentAngle;
          currentAngle += angle;
          
          const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
          const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
          const x2 = 50 + 40 * Math.cos(((startAngle + angle) * Math.PI) / 180);
          const y2 = 50 + 40 * Math.sin(((startAngle + angle) * Math.PI) / 180);
          
          const largeArc = angle > 180 ? 1 : 0;
          
          if (percentage === 0) return null;
          
          return (
            <motion.path
              key={idx}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: idx * 0.2, duration: 0.5 }}
              d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={colors[idx]}
            />
          );
        })}
        <circle cx="50" cy="50" r="25" fill="white" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-navy-900">{total}</span>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = "gold", trend }) => {
  const colorClasses = {
    gold: "bg-gold-100 text-gold-600",
    emerald: "bg-emerald-100 text-emerald-600",
    blue: "bg-blue-100 text-blue-600",
    purple: "bg-purple-100 text-purple-600",
    red: "bg-red-100 text-red-600",
    amber: "bg-amber-100 text-amber-600"
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-elevated p-5"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-navy-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-navy-900">{value}</p>
          {subtitle && <p className="text-xs text-navy-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`mt-3 flex items-center gap-1 text-sm ${trend >= 0 ? "text-emerald-600" : "text-red-600"}`}>
          {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span>{Math.abs(trend)}% vs mes anterior</span>
        </div>
      )}
    </motion.div>
  );
};

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics`);
      setAnalytics(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!analytics) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-navy-500">Error al cargar analytics</p>
        </div>
      </DashboardLayout>
    );
  }

  const riskData = [
    { label: "Bajo", value: analytics.risk_distribution.low, color: "emerald" },
    { label: "Medio", value: analytics.risk_distribution.medium, color: "amber" },
    { label: "Alto", value: analytics.risk_distribution.high, color: "red" },
    { label: "Pendiente", value: analytics.risk_distribution.pending, color: "gray" }
  ];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
            Dashboard Analytics
          </h1>
          <p className="text-navy-600">
            Métricas y estadísticas del sistema
          </p>
        </motion.div>

        {/* Top Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Casos"
            value={analytics.summary.total_cases}
            subtitle={`${analytics.summary.active_cases} activos`}
            icon={FileText}
            color="gold"
          />
          <StatCard
            title="Tasa Aprobación"
            value={`${analytics.results.approval_rate}%`}
            subtitle={`${analytics.results.approved} aprobados`}
            icon={CheckCircle2}
            color="emerald"
          />
          <StatCard
            title="Total Usuarios"
            value={analytics.summary.total_users}
            subtitle={`${analytics.summary.premium_users} premium`}
            icon={Users}
            color="blue"
          />
          <StatCard
            title="Prob. Promedio"
            value={`${analytics.summary.avg_probability}%`}
            subtitle="Score promedio"
            icon={Target}
            color="purple"
          />
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Cases by Month */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-elevated p-6"
          >
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-gold-600" />
              <h3 className="font-semibold text-navy-900">Casos por Mes</h3>
            </div>
            <BarChart data={analytics.cases_by_month} />
          </motion.div>

          {/* Risk Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-elevated p-6"
          >
            <div className="flex items-center gap-2 mb-6">
              <PieChart className="w-5 h-5 text-gold-600" />
              <h3 className="font-semibold text-navy-900">Distribución de Riesgo</h3>
            </div>
            <div className="flex items-center justify-around">
              <DonutChart data={riskData} />
              <div className="space-y-3">
                {riskData.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${
                      item.color === "emerald" ? "bg-emerald-500" :
                      item.color === "amber" ? "bg-amber-500" :
                      item.color === "red" ? "bg-red-500" : "bg-gray-400"
                    }`}></div>
                    <span className="text-sm text-navy-600">{item.label}</span>
                    <span className="text-sm font-bold text-navy-900">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card-elevated p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Crown className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-navy-500">Plan Premium</p>
                <p className="text-lg font-bold text-navy-900">{analytics.summary.premium_users}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="card-elevated p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileSearch className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-navy-500">FOIA Pendientes</p>
                <p className="text-lg font-bold text-navy-900">{analytics.foia.pending}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="card-elevated p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-navy-500">Chats sin leer</p>
                <p className="text-lg font-bold text-navy-900">{analytics.chat.unread}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="card-elevated p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-navy-500">Alto Riesgo</p>
                <p className="text-lg font-bold text-navy-900">{analytics.risk_distribution.high}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Cases by Step */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="card-elevated p-6"
        >
          <div className="flex items-center gap-2 mb-6">
            <Activity className="w-5 h-5 text-gold-600" />
            <h3 className="font-semibold text-navy-900">Casos por Etapa del Proceso</h3>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
            {analytics.cases_by_step.map((step, idx) => (
              <div key={idx} className="text-center p-3 bg-navy-50 rounded-lg">
                <p className="text-2xl font-bold text-gold-600">{step.count}</p>
                <p className="text-xs text-navy-500 mt-1">{step.step}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Results Breakdown */}
        <div className="grid md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="card-elevated p-6 bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700">Aprobados</p>
                <p className="text-4xl font-bold text-emerald-600">{analytics.results.approved}</p>
              </div>
              <CheckCircle2 className="w-12 h-12 text-emerald-300" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="card-elevated p-6 bg-gradient-to-br from-red-50 to-orange-50 border-red-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Negados</p>
                <p className="text-4xl font-bold text-red-600">{analytics.results.denied}</p>
              </div>
              <AlertTriangle className="w-12 h-12 text-red-300" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="card-elevated p-6 bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">En Proceso</p>
                <p className="text-4xl font-bold text-amber-600">{analytics.results.processing}</p>
              </div>
              <Clock className="w-12 h-12 text-amber-300" />
            </div>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminAnalytics;
